import {
  CATEGORY_L2_ALLOWED_BY_L1,
  isCategoryL1,
  isCategoryL2,
  normalizeCategoryL1,
} from '../server/categoryTaxonomy';

export type SeoLanguage = 'en' | 'zh';

function normalizeSeoLanguage(value: unknown, fallback: SeoLanguage): SeoLanguage {
  if (value === 'en' || value === 'zh') return value;
  return fallback;
}

function pickCanonicalL1(url: URL): string | null {
  const l1Raw = url.searchParams.get('l1');
  if (l1Raw && l1Raw !== 'all' && isCategoryL1(l1Raw)) {
    return l1Raw;
  }

  const legacyCategoryRaw = url.searchParams.get('category');
  if (!legacyCategoryRaw) return null;

  const mapped = normalizeCategoryL1(legacyCategoryRaw);
  const normalizedLegacy = legacyCategoryRaw.trim().toLowerCase().replace(/[\s-]+/g, '_');
  const shouldKeepPending = normalizedLegacy === 'pending' || normalizedLegacy === 'other';
  if (mapped === 'pending' && !shouldKeepPending) {
    return null;
  }
  return mapped;
}

function pickCanonicalL2(url: URL, l1: string | null): string | null {
  if (!l1 || !isCategoryL1(l1)) return null;

  const l2Raw = url.searchParams.get('l2');
  if (!l2Raw || l2Raw === 'all' || !isCategoryL2(l2Raw)) {
    return null;
  }

  const allowed = CATEGORY_L2_ALLOWED_BY_L1[l1];
  if (!allowed.includes(l2Raw)) {
    return null;
  }

  return l2Raw;
}

function normalizeCanonicalParams(url: URL): URL {
  const normalized = new URL(url.toString());
  normalized.hash = '';

  const page = normalized.searchParams.get('page') === 'main' ? 'main' : null;
  const l1 = page ? pickCanonicalL1(normalized) : null;
  const l2 = page ? pickCanonicalL2(normalized, l1) : null;

  const params = new URLSearchParams();
  if (page) params.set('page', page);
  if (l1) params.set('l1', l1);
  if (l2) params.set('l2', l2);

  normalized.search = params.toString();
  return normalized;
}

export function buildCanonicalSeoUrl(currentUrl: string | URL, language: SeoLanguage): URL {
  const url = typeof currentUrl === 'string' ? new URL(currentUrl) : new URL(currentUrl.toString());
  const normalized = normalizeCanonicalParams(url);
  normalized.searchParams.set('lang', normalizeSeoLanguage(language, 'en'));
  return normalized;
}

export function buildXDefaultSeoUrl(currentUrl: string | URL): URL {
  const url = typeof currentUrl === 'string' ? new URL(currentUrl) : new URL(currentUrl.toString());
  return normalizeCanonicalParams(url);
}
